import cx from 'clsx';
import * as React from 'react';

import { mapAbortedToNull } from '../coreUtils/async';
import { EventObserver } from '../coreUtils/events';
import { Translation } from '../coreUtils/i18n';

import { PlaceholderEntityType } from '../data/schema';
import type { ElementModel, ElementTypeIri } from '../data/model';
import type { DataProviderLookupItem } from '../data/dataProvider';
import type { MetadataCreatedEntity } from '../data/metadataProvider';

import { HtmlSpinner } from '../diagram/spinner';

import type { DataDiagramModel } from '../editor/dataDiagramModel';
import { EntityElement } from '../editor/dataElements';

import { ListElementView } from '../widgets/utility/listElementView';
import { NoSearchResults } from '../widgets/utility/noSearchResults';
import { SearchInput, SearchInputStore, useSearchInputStore } from '../widgets/utility/searchInput';
import { createRequest } from '../widgets/instancesSearch';

import { type WorkspaceContext, useWorkspace } from '../workspace/workspaceContext';

export interface ElementTypeSelectorProps {
    source: ElementModel;
    elementValue: ElementValue;
    onChange: (state: Pick<ElementValue, 'value' | 'isNew' | 'loading'>) => void;
}

export interface ElementValue {
    value: MetadataCreatedEntity;
    isNew: boolean;
    loading: boolean;
    error?: string;
    validated: boolean;
    allowChange: boolean;
}

export function ElementTypeSelector(props: ElementTypeSelectorProps) {
    const searchStore = useSearchInputStore({
        initialValue: '',
        submitTimeout: 200,
    });
    const workspace = useWorkspace();
    return (
        <ElementTypeSelectorInner {...props}
            searchStore={searchStore}
            workspace={workspace}
        />
    );
}

interface ElementTypeSelectorInnerProps extends ElementTypeSelectorProps {
    searchStore: SearchInputStore;
    workspace: WorkspaceContext;
}

interface State {
    elementTypes?: ReadonlyArray<ElementTypeIri>;
    elementTypesState: 'none' | 'loading' | 'error';
    existingElements: ReadonlyArray<ElementModel>;
    existingElementsState: 'none' | 'loading' | 'error';
}

const CLASS_NAME = 'reactodia-element-selector';
const FORM_CLASS = 'reactodia-form';

export class ElementTypeSelectorInner extends React.Component<ElementTypeSelectorInnerProps, State> {
    private readonly listener = new EventObserver();
    private fetchTypesCancellation = new AbortController();
    private filterCancellation = new AbortController();
    private loadingItemCancellation = new AbortController();

    constructor(props: ElementTypeSelectorInnerProps) {
        super(props);
        this.state = {
            elementTypesState: 'none',
            existingElements: [],
            existingElementsState: 'none',
        };
    }

    componentDidMount() {
        const {searchStore, workspace: {model}} = this.props;

        this.listener.listen(model.events, 'elementTypeEvent', ({data}) => {
            const {elementTypes} = this.state;
            const changeEvent = data.changeData;
            if (changeEvent && elementTypes && elementTypes.includes(changeEvent.source.id)) {
                this.forceUpdate();
            }
        });
        this.listener.listen(searchStore.events, 'changeValue', ({source}) => {
            if (source.value.length === 0) {
                // Clear the search
                void this.searchExistingElements('');
            } else {
                this.forceUpdate();
            }
        });
        this.listener.listen(searchStore.events, 'executeSearch', ({value}) => {
            void this.searchExistingElements(value);
        });

        void this.fetchPossibleElementTypes();
    }

    componentWillUnmount() {
        this.listener.stopListening();
        this.fetchTypesCancellation.abort();
        this.filterCancellation.abort();
        this.loadingItemCancellation.abort();
    }

    private async fetchPossibleElementTypes() {
        const {
            source, workspace: {model, editor: {metadataProvider}, translation: t},
        } = this.props;
        if (!metadataProvider) {
            return;
        }

        this.fetchTypesCancellation.abort();
        this.fetchTypesCancellation = new AbortController();
        const signal = this.fetchTypesCancellation.signal;

        const connections = await mapAbortedToNull(
            metadataProvider.canConnect(
                source,
                undefined,
                undefined,
                {signal}
            ),
            signal
        );
        if (connections === null) {
            return;
        }
        const elementTypeSet = new Set<ElementTypeIri>();
        for (const {targetTypes} of connections) {
            for (const type of targetTypes) {
                elementTypeSet.add(type);
            }
        }
        const elementTypes = Array.from(elementTypeSet)
            .sort(makeElementTypeComparatorByLabel(model, t));
        this.setState({elementTypes});
    }

    private async searchExistingElements(searchString: string) {
        const {workspace: {model}} = this.props;

        this.filterCancellation.abort();
        this.setState({existingElements: [], existingElementsState: 'none'});

        if (searchString.length > 0) {
            this.setState({existingElementsState: 'loading'});
            this.filterCancellation = new AbortController();
            const signal = this.filterCancellation.signal;

            const request = createRequest({text: searchString});
            let lookupItems: DataProviderLookupItem[] | null;
            try {
                lookupItems = await mapAbortedToNull(
                    model.dataProvider.lookup(request),
                    signal
                );
                if (lookupItems === null) {
                    return;
                }
            } catch (err) {
                console.error('Error looking up existing entities', err);
                this.setState({existingElementsState: 'error'});
                return;
            }

            const existingElements = lookupItems.map(linked => linked.element);
            this.setState({existingElements, existingElementsState: 'none'});
        }
    }

    private onElementTypeChange = async (e: React.FormEvent<HTMLSelectElement>) => {
        this.setState({elementTypesState: 'loading'});

        this.loadingItemCancellation.abort();
        this.loadingItemCancellation = new AbortController();
        const signal = this.loadingItemCancellation.signal;

        const {onChange, workspace: {editor: {metadataProvider}}} = this.props;
        const elementTypeIri: ElementTypeIri = (e.target as HTMLSelectElement).value;
        let createdEntity: MetadataCreatedEntity | null;
        try {
            createdEntity = await mapAbortedToNull(
                metadataProvider!.createEntity(elementTypeIri, {signal}),
                signal
            );
            if (createdEntity === null) {
                return;
            }
        } catch (err) {
            console.error('Error calling MetadataProvider.createEntity()', err);
            this.setState({elementTypesState: 'error'});
            return;
        }

        this.setState({elementTypesState: 'none'});
        onChange({
            value: createdEntity,
            isNew: true,
            loading: false,
        });
    };

    private renderPossibleElementType = (elementType: ElementTypeIri) => {
        const {workspace: {model, translation: t}} = this.props;
        const type = model.getElementType(elementType);
        const label = t.formatLabel(type?.data?.label, elementType, model.language);
        return (
            <option key={elementType} value={elementType}>
                {t.text('visual_authoring.select_entity.entity_type.label', {
                    type: label,
                    typeIri: elementType,
                })}
            </option>
        );
    };

    private renderElementTypeSelector() {
        const {elementValue, workspace: {translation: t}} = this.props;
        const {elementTypes, elementTypesState} = this.state;
        const value = elementValue.value.data.types.length ? elementValue.value.data.types[0] : '';
        if (elementTypesState !== 'none') {
            return (
                <HtmlSpinner width={20} height={20}
                    errorOccurred={elementTypesState === 'error'}
                />
            );
        }
        return (
            <div className={`${FORM_CLASS}__control-row`}>
                <label>{t.text('visual_authoring.select_entity.type.label')}</label>
                {
                    elementTypes ? (
                        <select className='reactodia-form-control'
                            name='reactodia-element-type-selector-select'
                            value={value}
                            onChange={e => void this.onElementTypeChange(e)}>
                            <option value={PlaceholderEntityType} disabled={true}>
                                {t.text('visual_authoring.select_entity.type.placeholder')}
                            </option>
                            {
                                elementTypes.map(this.renderPossibleElementType)
                            }
                        </select>
                    ) : <div><HtmlSpinner width={20} height={20} /></div>
                }
                {elementValue.error ? <span className={`${FORM_CLASS}__control-error`}>{elementValue.error}</span> : ''}
            </div>
        );
    }

    private renderExistingElementsList() {
        const {elementValue,  workspace: {model, editor}} = this.props;
        const {elementTypes, existingElements, existingElementsState} = this.state;
        if (existingElementsState !== 'none') {
            return (
                <div className={`${CLASS_NAME}__results-spinner`}>
                    <HtmlSpinner width={30} height={30}
                        errorOccurred={existingElementsState === 'error'}
                    />
                </div>
            );
        }
        if (existingElements.length > 0) {
            return existingElements.map(element => {
                const isAlreadyOnDiagram = !editor.temporaryState.elements.has(element.id) && Boolean(
                    model.elements.find((el) => el instanceof EntityElement && el.iri === element.id)
                );
                const hasAppropriateType = Boolean(
                    elementTypes && elementTypes.find(type => element.types.indexOf(type) >= 0)
                );
                return (
                    <ListElementView key={element.id}
                        element={element}
                        disabled={isAlreadyOnDiagram || !hasAppropriateType}
                        selected={element.id === elementValue.value.data.id}
                        onClick={(e, model) => void this.onSelectExistingItem(model)}
                    />
                );
            });
        }
        return <NoSearchResults hasQuery={true} />;
    }

    private async onSelectExistingItem(data: ElementModel) {
        const {onChange, workspace: {model}} = this.props;

        this.loadingItemCancellation.abort();
        this.loadingItemCancellation = new AbortController();
        const signal = this.loadingItemCancellation.signal;

        onChange({value: {data}, isNew: false, loading: true});
        const result = await model.dataProvider.elements({elementIds: [data.id]});
        if (signal.aborted) { return; }

        const loadedModel = result.get(data.id)!;
        onChange({value: {data: loadedModel}, isNew: false, loading: false});
    }

    render() {
        const {searchStore, workspace: {translation: t}} = this.props;
        return (
            <div
                className={cx(
                    `${FORM_CLASS}__row`,
                    CLASS_NAME,
                    searchStore.value.length > 0 ? `${CLASS_NAME}--search-existing` : undefined
                )}>
                <SearchInput store={searchStore}
                    className={`${CLASS_NAME}__search`}
                    inputProps={{
                        className: `${CLASS_NAME}__search-input`,
                        name: 'reactodia-element-type-selector-search',
                        autoFocus: true,
                    }}>
                    <span className={`${CLASS_NAME}__search-icon`} />
                </SearchInput>
                {
                    searchStore.value.length > 0 ? (
                        <div className={`${CLASS_NAME}__existing-elements-list`}
                            role='listbox'
                            aria-label={t.text('visual_authoring.select_entity.results.aria_label')}>
                            {this.renderExistingElementsList()}
                        </div>
                    ) : (
                        <div>
                            <div className={`${CLASS_NAME}__separator`}>
                                <i className={`${CLASS_NAME}__separator-text`}>
                                    {t.text('visual_authoring.select_entity.separator.label')}
                                </i>
                            </div>
                            {this.renderElementTypeSelector()}
                        </div>
                    )
                }
            </div>
        );
    }
}

function makeElementTypeComparatorByLabel(model: DataDiagramModel, t: Translation) {
    return (a: ElementTypeIri, b: ElementTypeIri) => {
        const typeA = model.getElementType(a);
        const typeB = model.getElementType(b);
        const labelA = t.formatLabel(typeA?.data?.label, a, model.language);
        const labelB = t.formatLabel(typeB?.data?.label, b, model.language);
        return labelA.localeCompare(labelB);
    };
}

export function validateElementType(
    element: ElementModel,
    workspace: WorkspaceContext
): Promise<Pick<ElementValue, 'error' | 'allowChange'>> {
    const {translation: t} = workspace;
    const isElementTypeSelected = element.types.indexOf(PlaceholderEntityType) < 0;
    const error = isElementTypeSelected
        ? undefined
        : t.text('visual_authoring.select_entity.validation.error_required');
    return Promise.resolve({error, allowChange: true});
}
